/**
 * @file apps/web/src/components/RoutingBuilder.tsx
 * @description Drag-free approval routing builder.
 * Allows adding approvers, configuring workflow type (sequential/parallel/conditional),
 * setting parallel groups, and defining condition expressions per step.
 * Outputs a step list compatible with POST /approvals.
 */

import { useState } from 'react';
import {
  Plus, Trash2, ChevronUp, ChevronDown, GitBranch, Users, ArrowDown, Settings,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RoutingStep {
  tempId:        number;
  approverId:    string;
  approverName:  string;
  approverEmail: string;
  parallelGroup?: number;
  conditionExpr?: ConditionExpr;
  isRequired:    boolean;
}

export interface ConditionExpr {
  field: string;
  op:    'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'nin';
  value: string | number;
}

export interface TeamMember {
  id:        string;
  full_name?: string | null;
  email:     string;
}

export type WorkflowType = 'sequential' | 'parallel' | 'conditional';

interface RoutingBuilderProps {
  teamMembers:    TeamMember[];
  value:          RoutingStep[];
  workflowType:   WorkflowType;
  onChange:       (steps: RoutingStep[], type: WorkflowType) => void;
  className?:     string;
  /** Restricts which workflow-type buttons are shown. Defaults to all three. */
  allowedTypes?:  WorkflowType[];
}

// ---------------------------------------------------------------------------
// Condition editor
// ---------------------------------------------------------------------------

const CONDITION_FIELDS: { value: string; label: string }[] = [
  { value: 'metadata.amount',       label: 'Contract Value' },
  { value: 'metadata.risk_level',   label: 'Project Risk Level' },
  { value: 'metadata.module_count', label: 'Module Count' },
  { value: 'metadata.custom',       label: 'Custom Field' },
];

const OPS = ['gt','gte','lt','lte','eq','neq'] as const;
const OP_LABELS: Record<string, string> = {
  gt:  'is greater than',
  gte: 'is greater than or equal to',
  lt:  'is less than',
  lte: 'is less than or equal to',
  eq:  'equals',
  neq: 'is not equal to',
};

function ConditionEditor({
  value,
  onChange,
}: {
  value?:    ConditionExpr;
  onChange:  (expr: ConditionExpr | undefined) => void;
}) {
  const [enabled, setEnabled] = useState(!!value);
  const expr: ConditionExpr = value ?? { field: 'metadata.amount', op: 'gt', value: 0 };

  const update = (patch: Partial<ConditionExpr>) => {
    onChange({ ...expr, ...patch });
  };

  const fieldLabel = CONDITION_FIELDS.find((f) => f.value === expr.field)?.label ?? expr.field;
  const opLabel    = OP_LABELS[expr.op] ?? expr.op;

  return (
    <div className="mt-2 pt-2 border-t border-[rgba(8,17,31,0.07)]">
      <label className="flex items-center gap-2 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked);
            onChange(e.target.checked ? expr : undefined);
          }}
          className="rounded border-gray-300 text-blue focus:ring-blue"
        />
        <span className="text-xs font-mono text-ink/70">Only activate if condition is met</span>
      </label>
      {enabled && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={expr.field}
              onChange={(e) => update({ field: e.target.value })}
              className="select text-xs py-1"
            >
              {CONDITION_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <select
              value={expr.op}
              onChange={(e) => update({ op: e.target.value as ConditionExpr['op'] })}
              className="select text-xs py-1"
            >
              {OPS.map((op) => (
                <option key={op} value={op}>{OP_LABELS[op]}</option>
              ))}
            </select>
            <input
              type="text"
              value={String(expr.value)}
              onChange={(e) => update({ value: e.target.value })}
              placeholder="Enter value"
              className="input text-xs py-1 w-28"
            />
          </div>
          <p className="text-[11px] font-mono text-muted">
            Only activate if <span className="text-ink font-semibold">{fieldLabel}</span>{' '}
            <span className="text-blue">{opLabel}</span>{' '}
            <span className="text-ink font-semibold">{String(expr.value) || '…'}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step card
// ---------------------------------------------------------------------------

let _tempId = 1;
function nextTempId() { return _tempId++; }

function StepCard({
  step,
  index,
  total,
  workflowType,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  step:         RoutingStep;
  index:        number;
  total:        number;
  workflowType: WorkflowType;
  onUpdate:     (patch: Partial<RoutingStep>) => void;
  onRemove:     () => void;
  onMoveUp:     () => void;
  onMoveDown:   () => void;
}) {
  const [showCondition, setShowCondition] = useState(!!step.conditionExpr);

  return (
    <div className="card p-3">
      <div className="flex items-start gap-2">
        {/* Step number */}
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-mono font-bold text-white shrink-0 mt-0.5"
          style={{ backgroundColor: 'var(--blue)' }}
        >
          {index + 1}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm font-medium text-ink">
              {step.approverName || step.approverEmail}
            </span>
            <span className="text-[11px] font-mono text-muted">
              {step.approverEmail !== step.approverName ? step.approverEmail : ''}
            </span>
          </div>

          {/* Parallel group (parallel workflow only) */}
          {workflowType === 'parallel' && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-mono text-muted">Group:</span>
              <input
                type="number"
                min={1}
                value={step.parallelGroup ?? 1}
                onChange={(e) => onUpdate({ parallelGroup: parseInt(e.target.value, 10) || 1 })}
                className="input text-xs py-0.5 w-16"
              />
              <span className="text-[11px] text-muted">(same group = activated in parallel)</span>
            </div>
          )}

          {/* Conditional expression */}
          {workflowType === 'conditional' && (
            <ConditionEditor
              value={step.conditionExpr}
              onChange={(expr) => onUpdate({ conditionExpr: expr })}
            />
          )}

          {/* Required toggle */}
          <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={step.isRequired}
              onChange={(e) => onUpdate({ isRequired: e.target.checked })}
              className="rounded border-gray-300 text-blue focus:ring-blue"
            />
            <span className="text-xs text-ink/60">Required (rejection blocks workflow)</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-0.5 shrink-0">
          <button onClick={onMoveUp}   disabled={index === 0}     className="btn-icon w-6 h-6 text-muted disabled:opacity-30">
            <ChevronUp size={12} />
          </button>
          <button onClick={onMoveDown} disabled={index === total - 1} className="btn-icon w-6 h-6 text-muted disabled:opacity-30">
            <ChevronDown size={12} />
          </button>
          <button onClick={onRemove}   className="btn-icon w-6 h-6 text-muted hover:text-danger">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkflowTypeSelector
// ---------------------------------------------------------------------------

const WORKFLOW_TYPES: { value: WorkflowType; label: string; desc: string; icon: React.ReactNode }[] = [
  {
    value: 'sequential',
    label: 'Sequential',
    desc:  'Approvers sign off one at a time in order.',
    icon:  <ArrowDown size={14} />,
  },
  {
    value: 'parallel',
    label: 'Parallel',
    desc:  'Multiple approvers are notified simultaneously (grouped).',
    icon:  <Users size={14} />,
  },
  {
    value: 'conditional',
    label: 'Conditional',
    desc:  'Steps are activated or skipped based on conditions.',
    icon:  <GitBranch size={14} />,
  },
];

// ---------------------------------------------------------------------------
// RoutingBuilder
// ---------------------------------------------------------------------------

export function RoutingBuilder({
  teamMembers,
  value,
  workflowType,
  onChange,
  className = '',
  allowedTypes,
}: RoutingBuilderProps) {
  const [addingApprover, setAddingApprover] = useState(false);

  const updateStep = (tempId: number, patch: Partial<RoutingStep>) => {
    onChange(
      value.map((s) => s.tempId === tempId ? { ...s, ...patch } : s),
      workflowType,
    );
  };

  const removeStep = (tempId: number) => {
    onChange(value.filter((s) => s.tempId !== tempId), workflowType);
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const arr = [...value];
    const [item] = arr.splice(index, 1);
    arr.splice(index + dir, 0, item!);
    onChange(arr, workflowType);
  };

  const addMember = (member: TeamMember) => {
    const newStep: RoutingStep = {
      tempId:        nextTempId(),
      approverId:    member.id,
      approverName:  member.full_name ?? member.email,
      approverEmail: member.email,
      parallelGroup: workflowType === 'parallel' ? 1 : undefined,
      isRequired:    true,
    };
    onChange([...value, newStep], workflowType);
    setAddingApprover(false);
  };

  const alreadyAdded = new Set(value.map((s) => s.approverId));

  const visibleTypes = allowedTypes
    ? WORKFLOW_TYPES.filter((wt) => allowedTypes.includes(wt.value))
    : WORKFLOW_TYPES;

  return (
    <div className={['space-y-4', className].join(' ')}>
      {/* Workflow type selector */}
      <div>
        <p className="label mb-2">Workflow Type</p>
        <div className={visibleTypes.length === 2 ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-3 gap-2'}>
          {visibleTypes.map((wt) => (
            <button
              key={wt.value}
              onClick={() => onChange(value, wt.value)}
              className={[
                'flex flex-col items-start gap-1 p-3 rounded-lg border-2 text-left transition-colors',
                workflowType === wt.value
                  ? 'border-blue bg-sky'
                  : 'border-[rgba(8,17,31,0.1)] bg-white hover:border-blue/40 hover:bg-sky/40',
              ].join(' ')}
            >
              <div className={['flex items-center gap-1.5 font-medium text-sm',
                workflowType === wt.value ? 'text-blue' : 'text-ink'].join(' ')}>
                {wt.icon}
                {wt.label}
              </div>
              <p className="text-[11px] text-muted leading-snug">{wt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Step list */}
      <div>
        <p className="label mb-2">Approvers ({value.length})</p>
        <div className="space-y-2">
          {value.map((step, i) => (
            <StepCard
              key={step.tempId}
              step={step}
              index={i}
              total={value.length}
              workflowType={workflowType}
              onUpdate={(patch) => updateStep(step.tempId, patch)}
              onRemove={() => removeStep(step.tempId)}
              onMoveUp={() => moveStep(i, -1)}
              onMoveDown={() => moveStep(i, 1)}
            />
          ))}

          {value.length === 0 && (
            <p className="text-sm text-muted text-center py-4 border-2 border-dashed border-[rgba(8,17,31,0.1)] rounded-lg">
              Add at least one approver.
            </p>
          )}
        </div>
      </div>

      {/* Add approver */}
      {!addingApprover ? (
        <button
          onClick={() => setAddingApprover(true)}
          className="btn-ghost w-full justify-center border border-dashed border-[rgba(8,17,31,0.12)] py-2"
        >
          <Plus size={14} /> Add Approver
        </button>
      ) : (
        <div className="card p-3">
          <p className="label mb-2">Select team member</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {teamMembers
              .filter((m) => !alreadyAdded.has(m.id))
              .map((m) => (
                <button
                  key={m.id}
                  onClick={() => addMember(m)}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded hover:bg-sky transition-colors"
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-mono font-bold shrink-0"
                    style={{ backgroundColor: 'var(--blue)' }}
                  >
                    {(m.full_name ?? m.email).slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">{m.full_name ?? m.email}</p>
                    <p className="text-[11px] font-mono text-muted">{m.email}</p>
                  </div>
                </button>
              ))
            }
            {teamMembers.filter((m) => !alreadyAdded.has(m.id)).length === 0 && (
              <p className="text-sm text-muted px-3 py-2">All team members added.</p>
            )}
          </div>
          <button onClick={() => setAddingApprover(false)} className="btn-ghost mt-2 text-xs">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
